<template>
  <NcrmRecordEditDialog
    :model-value="modelValue"
    :title="editing ? $t('recruitment.list.edit') : $t('recruitment.list.add')"
    :subtitle="$t('recruitment.requirementDrawer.subtitle')"
    header-icon="mdi-clipboard-text-search-outline"
    :loading="saving"
    :save-disabled="!canSave"
    max-width="680"
    @update:model-value="$emit('update:modelValue', $event)"
    @save="save"
  >
    <div class="requirement-drawer pa-6">
      <v-alert v-if="errorMessage" type="error" density="compact" class="mb-5">
        {{ errorMessage }}
      </v-alert>
      <section class="requirement-drawer__section">
        <div class="requirement-drawer__section-title">
          {{ $t("recruitment.requirementDrawer.requirementSection") }}
        </div>
        <v-autocomplete
          v-model="form.id_companies"
          :items="companies"
          item-title="company_name"
          item-value="id"
          :menu-props="{ maxHeight: 320, zIndex: 2710 }"
          :label="$t('recruitment.list.form.company')"
          :readonly="editing"
          :disabled="editing"
          class="mb-4"
          @update:model-value="onTenantChanged"
        />
        <div class="text-caption mb-2">{{ $t("recruitment.requirementDrawer.type") }}</div>
        <NcrmSegmentedControl
          block
          class="mb-4"
          :model-value="form.requirement_type"
          :items="typeItems"
          :aria-label="$t('recruitment.requirementDrawer.type')"
          :disabled="typeAndClientLocked"
          @update:model-value="setRequirementType"
        />
        <v-text-field
          v-model.trim="form.name_vacancy"
          :label="$t('recruitment.list.form.position')"
          :rules="[requiredRule]"
        />
      </section>
      <template v-if="form.requirement_type === 'client'">
        <v-divider class="my-6" />
        <section class="requirement-drawer__section">
          <div class="requirement-drawer__section-title">
            {{ $t("recruitment.requirementDrawer.clientSection") }}
          </div>
          <v-alert v-if="typeAndClientLocked" type="info" density="compact" class="mb-4">
            {{ $t("recruitment.requirementDrawer.locked") }}
          </v-alert>
          <template v-if="!creatingCompany">
            <v-autocomplete
              v-model="form.ncrm_client_company_id"
              v-model:search="clientCompanySearch"
              :items="clientCompanies"
              item-title="name"
              item-value="id"
              :menu-props="{ maxHeight: 320, zIndex: 2710 }"
              :label="$t('recruitment.requirementDrawer.clientCompany')"
              :loading="loadingCompanies"
              :disabled="typeAndClientLocked"
              clearable
              class="mb-2"
              @update:model-value="onClientCompanyChanged"
              @update:search="scheduleClientCompanySearch"
            />
            <v-btn
              v-if="!typeAndClientLocked"
              variant="text"
              size="small"
              prepend-icon="mdi-plus"
              class="mb-4"
              @click="startCreatingCompany"
            >
              {{ $t("recruitment.requirementDrawer.createCompany") }}
            </v-btn>
          </template>
          <RecruitmentNewCompanyEditor
            v-else
            v-model="newCompany.name"
            @close="cancelCreatingCompany"
          />
          <v-autocomplete
            v-if="!creatingCompany"
            v-model="selectedContactIds"
            v-model:search="clientPeopleSearch"
            :items="clientPeople"
            :item-title="personLabelWithCompany"
            item-value="id"
            :menu-props="{ maxHeight: 320, zIndex: 2710 }"
            :label="$t('recruitment.requirementDrawer.contacts')"
            multiple
            chips
            closable-chips
            :loading="loadingPeople"
            class="mb-3"
            @update:search="scheduleClientPeopleSearch"
          />
          <v-alert v-if="duplicateWarning" type="warning" density="compact" class="mb-4">
            {{ $t("recruitment.requirementDrawer.duplicateWarning") }}
          </v-alert>
          <div class="d-flex align-center justify-space-between mb-3">
            <div class="requirement-drawer__subsection-title">
              {{ $t("recruitment.requirementDrawer.newContacts") }}
            </div>
            <v-btn variant="text" size="small" prepend-icon="mdi-account-plus-outline" @click="addNewContact">
              {{ $t("recruitment.requirementDrawer.addContact") }}
            </v-btn>
          </div>
          <RecruitmentContactEditor
            v-for="(contact, index) in newContacts"
            :key="contact.localId"
            :model-value="contact"
            :index="index"
            @update:model-value="newContacts[index] = $event"
            @remove="removeNewContact(index)"
          />
          <v-alert v-if="!selectedContactIds.length && !validNewContacts.length" type="info" density="compact">
            {{ $t("recruitment.requirementDrawer.contactRequired") }}
          </v-alert>
        </section>
      </template>
    </div>
  </NcrmRecordEditDialog>
</template>
<script>
import axios from "axios";
import NcrmRecordEditDialog from "@/components/ncrm/shared/NcrmRecordEditDialog.vue";
import NcrmSegmentedControl from "@/components/ncrm/shared/NcrmSegmentedControl.vue";
import RecruitmentContactEditor from "@/components/recruitment/RecruitmentContactEditor.vue";
import RecruitmentNewCompanyEditor from "@/components/recruitment/RecruitmentNewCompanyEditor.vue";
import {
  buildRecruitmentRequirementPayload,
  createEmptyRecruitmentContact,
  hasRecruitmentContactDuplicate,
  normalizeRequirementType,
} from "@/Utile/recruitmentSubmissions.mjs";
export default {
  name: "RecruitmentRequirementDrawer",
  components: { NcrmRecordEditDialog, NcrmSegmentedControl, RecruitmentContactEditor, RecruitmentNewCompanyEditor },
  props: {
    modelValue: { type: Boolean, default: false },
    record: { type: Object, default: null },
    companies: { type: Array, default: () => [] },
  },
  emits: ["update:modelValue", "saved", "notify"],
  data() {
    return {
      saving: false,
      loadingCompanies: false,
      loadingPeople: false,
      errorMessage: "",
      form: {
        id: null,
        id_companies: null,
        name_vacancy: "",
        requirement_type: "internal",
        ncrm_client_company_id: null,
        has_submissions: false,
      },
      clientCompanies: [],
      clientPeople: [],
      selectedContactIds: [],
      creatingCompany: false,
      newCompany: { name: "" },
      newContacts: [],
      clientCompanySearch: "",
      clientPeopleSearch: "",
      companySearchTimer: null,
      peopleSearchTimer: null,
    };
  },
  computed: {
    editing() {
      return Boolean(this.form.id);
    },
    typeItems() {
      return [
        { value: "internal", icon: "mdi-account-group-outline", label: this.$t("recruitment.requirementDrawer.internal") },
        { value: "client", icon: "mdi-office-building-outline", label: this.$t("recruitment.requirementDrawer.client") },
      ];
    },
    typeAndClientLocked() {
      return this.editing && Boolean(this.form.has_submissions);
    },
    validNewContacts() {
      return this.newContacts.filter((contact) =>
        contact.first_name && contact.last_name && this.isValidEmail(contact.email_primary)
      );
    },
    duplicateWarning() {
      return hasRecruitmentContactDuplicate(this.clientPeople, this.newContacts);
    },
    canSave() {
      if (!this.form.id_companies || !String(this.form.name_vacancy || "").trim()) return false;
      if (this.form.requirement_type !== "client") return true;
      const hasValidNewCompany = !this.creatingCompany || Boolean(String(this.newCompany.name || "").trim());
      const contactCount = this.selectedContactIds.length + this.validNewContacts.length;
      return hasValidNewCompany && this.newContacts.length === this.validNewContacts.length
        && contactCount > 0
        && contactCount <= 25;
    },
  },
  watch: {
    modelValue: {
      immediate: true,
      handler(open) {
        if (open) this.initialize();
      },
    },
  },
  beforeUnmount() {
    clearTimeout(this.companySearchTimer);
    clearTimeout(this.peopleSearchTimer);
  },
  methods: {
    requiredRule(value) {
      return Boolean(String(value || "").trim()) || this.$t("crm.callDialog.required");
    },
    isValidEmail(value) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || "").trim());
    },
    personLabel(person) {
      return `${[person.first_name, person.last_name].filter(Boolean).join(" ")} · ${person.email_primary || "—"}`;
    },
    personLabelWithCompany(person) {
      const company = person.ncrm_company_name ? ` · ${person.ncrm_company_name}` : "";
      return `${this.personLabel(person)}${company}`;
    },
    async initialize() {
      this.errorMessage = "";
      this.clientCompanies = [];
      this.clientPeople = [];
      this.selectedContactIds = [];
      this.creatingCompany = false;
      this.newCompany = { name: "" };
      this.newContacts = [];
      this.clientCompanySearch = "";
      this.clientPeopleSearch = "";

      let source = this.record || {};
      if (source.id) {
        try {
          const response = await axios.get(`/candidati/recruitment_requirements/${source.id}`);
          source = response.data || source;
        } catch (error) {
          this.errorMessage = error?.response?.data?.error || this.$t("recruitment.list.alerts.updateError");
        }
      }
      const firstCompany = this.companies.length === 1 ? this.companies[0].id : null;
      this.form = {
        id: source.id || null,
        id_companies: source.id_companies || firstCompany,
        name_vacancy: source.name_vacancy || "",
        requirement_type: normalizeRequirementType(source.requirement_type),
        ncrm_client_company_id: source.ncrm_client_company_id || null,
        has_submissions: Boolean(Number(source.has_submissions || 0)),
      };
      if (this.form.id_companies) await this.fetchClientCompanies();
      if (
        this.form.ncrm_client_company_id
        && source.client_company_name
        && !this.clientCompanies.some((company) => Number(company.id) === Number(this.form.ncrm_client_company_id))
      ) {
        this.clientCompanies.unshift({
          id: Number(this.form.ncrm_client_company_id),
          name: source.client_company_name,
        });
      }
      if (this.form.id_companies) {
        await this.fetchClientPeople();
      }
      this.selectedContactIds = (source.contacts || [])
        .filter((contact) => Number(contact.is_active) === 1 && contact.ncrm_person_id)
        .map((contact) => Number(contact.ncrm_person_id));
    },
    tenantQuery() {
      return { workspace_company_id: this.form.id_companies };
    },
    async fetchClientCompanies() {
      if (!this.form.id_companies) return;
      this.loadingCompanies = true;
      try {
        const response = await axios.get("/candidati/recruitment-client-directory/companies", {
          params: { ...this.tenantQuery(), search: this.clientCompanySearch || undefined },
        });
        this.clientCompanies = response.data || [];
      } catch (error) {
        this.errorMessage = error?.response?.data?.error || this.$t("recruitment.requirementDrawer.directoryError");
      } finally {
        this.loadingCompanies = false;
      }
    },
    async fetchClientPeople() {
      if (!this.form.id_companies) return;
      this.loadingPeople = true;
      try {
        const response = await axios.get("/candidati/recruitment-client-directory/people", {
          params: {
            ...this.tenantQuery(),
            ncrm_company_id: this.form.ncrm_client_company_id || undefined,
            search: this.clientPeopleSearch || undefined,
          },
        });
        this.clientPeople = response.data || [];
      } catch (error) {
        this.errorMessage = error?.response?.data?.error || this.$t("recruitment.requirementDrawer.directoryError");
      } finally {
        this.loadingPeople = false;
      }
    },
    onTenantChanged() {
      this.form.ncrm_client_company_id = null;
      this.selectedContactIds = [];
      this.clientPeople = [];
      this.fetchClientCompanies();
      this.fetchClientPeople();
    },
    setRequirementType(type) {
      this.form.requirement_type = type;
      this.onTypeChanged(type);
    },
    onTypeChanged(type) {
      if (type === "client" && this.form.id_companies) {
        this.fetchClientCompanies();
        this.fetchClientPeople();
      }
    },
    onClientCompanyChanged() {
      this.selectedContactIds = [];
      this.clientPeople = [];
      this.fetchClientPeople();
    },
    scheduleClientCompanySearch() {
      clearTimeout(this.companySearchTimer);
      this.companySearchTimer = setTimeout(() => this.fetchClientCompanies(), 250);
    },
    scheduleClientPeopleSearch() {
      clearTimeout(this.peopleSearchTimer);
      this.peopleSearchTimer = setTimeout(() => this.fetchClientPeople(), 250);
    },
    startCreatingCompany() {
      this.creatingCompany = true;
      this.form.ncrm_client_company_id = null;
      this.selectedContactIds = [];
      if (!this.newContacts.length) this.addNewContact();
    },
    cancelCreatingCompany() {
      this.creatingCompany = false;
      this.newCompany = { name: "" };
    },
    addNewContact() {
      if (this.selectedContactIds.length + this.newContacts.length >= 25) return;
      this.newContacts.push(createEmptyRecruitmentContact());
    },
    removeNewContact(index) {
      this.newContacts.splice(index, 1);
    },
    async save() {
      if (!this.canSave || this.saving) return;
      this.saving = true;
      this.errorMessage = "";
      const payload = buildRecruitmentRequirementPayload({
        form: this.form,
        companies: this.companies,
        creatingCompany: this.creatingCompany,
        newCompanyName: this.newCompany.name,
        selectedContactIds: this.selectedContactIds,
        newContacts: this.validNewContacts,
      });
      try {
        const response = this.editing
          ? await axios.put(`/candidati/recruitment_requirements/${this.form.id}`, payload)
          : await axios.post("/candidati/recruitment_requirements", payload);
        this.$emit("saved", response.data);
        this.$emit("update:modelValue", false);
      } catch (error) {
        this.errorMessage = error?.response?.data?.error
          || this.$t(this.editing ? "recruitment.list.alerts.updateError" : "recruitment.list.alerts.addError");
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
<style scoped src="./RecruitmentRequirementDrawer.css"></style>
